import React, { useEffect, useState } from 'react';
import { Box, Checkbox, HStack, Stack } from '@chakra-ui/react';
import Select from 'react-select';
import {
  ManagedSchoolsQuery,
  Period,
} from '../../../../../../generated/graphqlModels';
import { useTranslation } from 'react-i18next';

interface ProgramInfoProps {
  setFilteredSchools: (schools: ManagedSchoolsQuery['managedSchools']) => void;
  schools: ManagedSchoolsQuery['managedSchools'];
  activeOnlyDefault?: boolean;
  isLoading?: boolean;
}

const SchoolFilter = (props: ProgramInfoProps) => {
  const {
    setFilteredSchools,
    schools,
    activeOnlyDefault = false,
    isLoading,
  } = props;
  const [selectedPeriods, setSelectedPeriods] = useState<Period[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>('');
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>('');
  const [activeOnly, setActiveOnly] = useState(activeOnlyDefault);
  const { t } = useTranslation();
  const periods = schools.reduce((acc, school) => {
    (school.licenses || [(school as any).license]).forEach((license) => {
      if (acc.findIndex((l) => l.id === license.period.id) === -1) {
        acc.push(license.period);
      }
    });
    return acc;
  }, [] as Period[]);

  const programs = schools.reduce((acc, school) => {
    (school.licenses || [(school as any).license]).forEach((license) => {
      if (acc.findIndex((l) => l.id === license.program.id) === -1) {
        acc.push(license.program);
      }
    });
    return acc;
  }, [] as { id: string; name: string }[]);

  useEffect(() => {
    let filteredSchools: ManagedSchoolsQuery['managedSchools'] = [...schools];

    if (selectedSchoolId) {
      filteredSchools = [
        filteredSchools.find((s) => s.id === selectedSchoolId)!,
      ];
    }
    const date = new Date();
    filteredSchools = filteredSchools
      .map((school) => {
        if (!school) {
          return null;
        }
        const newLicenses = (school.licenses || [(school as any).license])
          .filter((l) => {
            if (selectedPeriods.length) {
              return selectedPeriods.includes(l.period.id);
            }
            return true;
          })
          .filter((l) => {
            if (activeOnly) {
              return date < new Date(l.period.lastDate);
            }
            return true;
          })
          .filter((l) => {
            if (selectedProgramId) {
              if (!l.program) {
                return false;
              }
              return l.program.id === selectedProgramId;
            }
            return true;
          });
        if (newLicenses.length === 0) {
          return null;
        }
        return {
          ...school,
          licenses: newLicenses,
          license: newLicenses[0],
        } as any;
      })
      .filter((v) => !!v);

    setFilteredSchools(filteredSchools as any);
  }, [selectedPeriods, selectedSchoolId, selectedProgramId, activeOnly]);

  return (
    <Stack
      color="black"
      alignItems={'center'}
      justifyContent="center"
      gap={4}
      spacing={0}
      direction={['column', 'column', 'column', 'row']}
      w="full"
      maxW="8xl"
    >
      <Box maxW="400px" w="full" onClick={(e) => e.stopPropagation()}>
        <Select
          isDisabled={isLoading}
          styles={{
            container: (provided) => ({
              ...provided,
              flex: 1,
            }),
          }}
          placeholder={t('filter.school')}
          options={schools.map((p) => ({ value: p.id, label: p.name }))}
          closeMenuOnSelect
          isClearable
          onChange={(o) => {
            if (o) {
              setSelectedSchoolId(o.value);
            } else {
              setSelectedSchoolId(null);
            }
          }}
          select
        />
      </Box>
      <Box maxW="400px" w="full" onClick={(e) => e.stopPropagation()}>
        <Select
          isDisabled={isLoading}
          styles={{
            container: (provided) => ({
              ...provided,
              flex: 1,
            }),
          }}
          placeholder={t('filter.program')}
          options={programs.map((p) => ({ value: p.id, label: p.name }))}
          closeMenuOnSelect
          isClearable
          onChange={(o) => {
            if (o) {
              setSelectedProgramId(o.value);
            } else {
              setSelectedProgramId(null);
            }
          }}
          select
        />
      </Box>
      <Box maxW="400px" w="full" onClick={(e) => e.stopPropagation()}>
        <Select
          isDisabled={isLoading}
          styles={{
            container: (provided) => ({
              ...provided,
              flex: 1,
            }),
          }}
          placeholder={t('filter.periods')}
          options={periods.map((p) => ({ value: p.id, label: p.name }))}
          closeMenuOnSelect={true}
          isMulti
          isClearable
          isOptionSelected={(option) => selectedPeriods.includes(option.value)}
          onChange={(values) => {
            if (values) {
              setSelectedPeriods(values.map((v) => v.value));
            } else {
              setSelectedPeriods([]);
            }
          }}
          select
        />
      </Box>
      <HStack w="full" mt={4} justifyContent="center">
        <Checkbox
          isChecked={activeOnly}
          onChange={() => {
            setActiveOnly(!activeOnly);
          }}
        />
        <span>{t('filter.activeOnly')}</span>
      </HStack>
    </Stack>
  );
};

export default SchoolFilter;
